import { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

import { TIP_PRESETS, TIP_LIMITS } from "../../constants/creditsConfig";
import {
  confirmTipPayment,
  createTipCheckout,
  fetchSavedTipCard,
  fetchTipJarFill,
} from "../../services/creditsService";
import { getNicheMeta } from "../niche/nicheMeta";
import "./TipJarSheet.css";

const stripePromiseCache = new Map();

function getStripePromise(publishableKey) {
  const key =
    publishableKey
    || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
    || "";
  if (!key) return null;
  if (!stripePromiseCache.has(key)) {
    stripePromiseCache.set(key, loadStripe(key));
  }
  return stripePromiseCache.get(key);
}

function formatCardBrand(brand) {
  if (!brand) return "Card";
  const map = {
    visa: "Visa",
    mastercard: "Mastercard",
    amex: "Amex",
    discover: "Discover",
    diners: "Diners",
    jcb: "JCB",
    unionpay: "UnionPay",
  };
  const key = String(brand).toLowerCase();
  return map[key] || brand.charAt(0).toUpperCase() + brand.slice(1);
}

function TipJarSvg({ fillPercent = 35, className = "" }) {
  const fillY = 55 - Math.round((fillPercent / 100) * 28);
  const fillHeight = 74 - fillY;

  return (
    <svg
      className={className}
      viewBox="0 0 72 80"
      fill="none"
      aria-hidden
    >
      <rect x="10" y="26" width="52" height="48" rx="10" fill="rgba(253,243,224,0.07)" stroke="rgba(255,255,255,0.14)" strokeWidth="1.5" />
      <clipPath id="tip-jar-clip">
        <rect x="11" y="27" width="50" height="46" rx="9" />
      </clipPath>
      <rect
        x="11"
        y={fillY}
        width="50"
        height={fillHeight}
        fill="rgba(196,121,10,0.18)"
        clipPath="url(#tip-jar-clip)"
      />
      <ellipse cx="28" cy="66" rx="8" ry="4.5" fill="rgba(196,121,10,0.32)" stroke="rgba(196,121,10,0.38)" strokeWidth="1" />
      <ellipse cx="44" cy="66" rx="7" ry="4" fill="rgba(196,121,10,0.25)" stroke="rgba(196,121,10,0.28)" strokeWidth="1" />
      <rect x="16" y="17" width="40" height="13" rx="5" fill="rgba(253,243,224,0.07)" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" />
      <rect x="10" y="11" width="52" height="11" rx="5.5" fill="#C4790A" opacity="0.88" />
      <rect x="22" y="8" width="28" height="6" rx="3" fill="#8B4A00" opacity="0.65" />
      <rect x="28" y="11" width="16" height="3" rx="1.5" fill="rgba(0,0,0,0.22)" />
      <rect x="15" y="30" width="4" height="22" rx="2" fill="white" opacity="0.1" />
    </svg>
  );
}

function MiniJarIcon() {
  return (
    <svg width="32" height="36" viewBox="0 0 72 80" fill="none" aria-hidden>
      <rect x="10" y="26" width="52" height="48" rx="10" fill="rgba(253,243,224,0.9)" stroke="#DDD8CE" strokeWidth="2" />
      <rect x="10" y="11" width="52" height="11" rx="5.5" fill="#C4790A" />
      <rect x="22" y="8" width="28" height="6" rx="3" fill="#A36008" />
    </svg>
  );
}

function TipPaymentForm({ email, onReady, onStripeRefs }) {
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    onStripeRefs?.({ stripe, elements });
    onReady?.(Boolean(stripe && elements));
  }, [stripe, elements, onReady, onStripeRefs]);

  return (
    <div className="tip-sheet-stripe-mount">
      <PaymentElement
        options={{
          layout: "tabs",
          defaultValues: {
            billingDetails: { email: email || undefined },
          },
        }}
      />
    </div>
  );
}

export function TipJarProfileRow({ creator, onOpen }) {
  const firstName = creator.displayName?.split(" ")[0] || "them";
  return (
    <button type="button" className="tip-profile-row" onClick={onOpen}>
      <span className="tip-profile-row-jar">
        <MiniJarIcon />
      </span>
      <span className="tip-profile-row-text">
        <span className="tip-profile-row-title">Leave a tip for {firstName}</span>
        <span className="tip-profile-row-sub">Coffee, slice, fancy dessert, or beaver 🍁</span>
      </span>
      <svg className="tip-profile-row-caret" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  );
}

export default function TipJarSheet({
  open,
  onClose,
  creator,
  dropId = null,
  currentUserId,
  userEmail = "",
}) {
  const [phase, setPhase] = useState("select"); // select | payment | success
  const [selected, setSelected] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fillPercent, setFillPercent] = useState(30);
  const [jarBounce, setJarBounce] = useState(false);
  const [showCoin, setShowCoin] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [publishableKey, setPublishableKey] = useState(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
  );
  const [paymentReady, setPaymentReady] = useState(false);
  const [stripeRef, setStripeRef] = useState({ stripe: null, elements: null });
  const [paidAmount, setPaidAmount] = useState(null);
  const [savedCard, setSavedCard] = useState(null);

  const meta = getNicheMeta(creator?.niche);
  const handle = creator?.handle || creator?.username || "";
  const { main, em } = useMemo(() => {
    const parts = (creator?.displayName || "Creator").trim().split(/\s+/);
    if (parts.length <= 1) return { main: parts[0] || "Creator", em: "" };
    return { main: parts.slice(0, -1).join(" "), em: parts[parts.length - 1] };
  }, [creator?.displayName]);

  useEffect(() => {
    if (!open || !(creator?.uid || creator?.id)) return;
    setPhase("select");
    setError("");
    setSelected(null);
    setCustomAmount("");
    setClientSecret(null);
    setPaymentIntentId(null);
    setPaymentReady(false);
    setPaidAmount(null);
    setSavedCard(null);
    const cid = creator.uid || creator.id;
    fetchTipJarFill(cid)
      .then((data) => setFillPercent(data?.fillPercent ?? 30))
      .catch(() => setFillPercent(30));
    if (currentUserId) {
      fetchSavedTipCard()
        .then((card) => setSavedCard(card))
        .catch(() => setSavedCard(null));
    }
  }, [open, creator?.uid, creator?.id, currentUserId]);

  const creatorId = creator?.uid || creator?.id;

  const activeAmount = useMemo(() => {
    if (selected) return selected.amount;
    const val = parseFloat(customAmount);
    if (val >= TIP_LIMITS.minCustom && val <= TIP_LIMITS.maxCustom) return val;
    return null;
  }, [selected, customAmount]);

  const stripePromise = useMemo(
    () => getStripePromise(publishableKey),
    [publishableKey]
  );

  const triggerJarAnim = () => {
    setShowCoin(true);
    setJarBounce(true);
    setFillPercent((p) => Math.min(100, p + 8));
    window.setTimeout(() => setShowCoin(false), 600);
    window.setTimeout(() => setJarBounce(false), 450);
  };

  const pickPreset = (preset) => {
    setSelected(preset);
    setCustomAmount("");
    triggerJarAnim();
  };

  const handleCustom = (value) => {
    setCustomAmount(value);
    setSelected(null);
  };

  const finishSuccess = (amount) => {
    setPaidAmount(amount);
    setPhase("success");
    triggerJarAnim();
    // Refresh saved card after first Element tip so next open can one-tap.
    fetchSavedTipCard()
      .then((card) => setSavedCard(card))
      .catch(() => {});
  };

  const startElementCheckout = async () => {
    if (!activeAmount || !creatorId || !currentUserId) return;
    if (currentUserId === creatorId) {
      setError("You cannot tip yourself.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      triggerJarAnim();
      const data = await createTipCheckout({
        creatorId,
        amount: activeAmount,
        anonymous,
        dropId,
        quickPay: false,
      });
      if (data.publishableKey) setPublishableKey(data.publishableKey);
      if (!data.clientSecret) {
        throw new Error("Payment could not be started.");
      }
      setClientSecret(data.clientSecret);
      setPaymentIntentId(data.paymentIntentId || null);
      setPaidAmount(activeAmount);
      setPhase("payment");
    } catch (err) {
      setError(err.message || "Could not start tip checkout.");
    } finally {
      setLoading(false);
    }
  };

  const handleQuickTip = async () => {
    if (!activeAmount || !creatorId || !currentUserId) return;
    if (currentUserId === creatorId) {
      setError("You cannot tip yourself.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      triggerJarAnim();
      const data = await createTipCheckout({
        creatorId,
        amount: activeAmount,
        anonymous,
        dropId,
        quickPay: true,
      });
      if (data.publishableKey) setPublishableKey(data.publishableKey);
      if (data.card) setSavedCard(data.card);

      if (data.quickPaid) {
        finishSuccess(data.amount || activeAmount);
        return;
      }

      if (data.requiresAction && data.clientSecret) {
        if (data.publishableKey) {
          // ensure stripe uses returned key before confirm
          setPublishableKey(data.publishableKey);
        }
        const key =
          data.publishableKey
          || publishableKey
          || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
          || "";
        const stripe = await getStripePromise(key);
        if (!stripe) throw new Error("Stripe could not load.");
        const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
          data.clientSecret
        );
        if (stripeError) {
          throw new Error(stripeError.message || "Card authentication failed.");
        }
        const piId = paymentIntent?.id || data.paymentIntentId;
        if (!piId) throw new Error("Missing payment confirmation.");
        const result = await confirmTipPayment({ paymentIntentId: piId });
        finishSuccess(result.amount || activeAmount);
        return;
      }

      // Fallback if quick pay couldn't run
      if (data.clientSecret) {
        setClientSecret(data.clientSecret);
        setPaymentIntentId(data.paymentIntentId || null);
        setPaidAmount(activeAmount);
        setPhase("payment");
        return;
      }

      throw new Error("Payment could not be started.");
    } catch (err) {
      setError(err.message || "Could not send tip.");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => {
    if (savedCard) return handleQuickTip();
    return startElementCheckout();
  };

  const handleConfirmPayment = async () => {
    const { stripe, elements } = stripeRef;
    if (!stripe || !elements) {
      setError("Payment form is still loading.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: typeof window !== "undefined" ? window.location.href : undefined,
        },
      });
      if (stripeError) {
        throw new Error(stripeError.message || "Payment failed.");
      }
      const piId = paymentIntent?.id || paymentIntentId;
      if (!piId) throw new Error("Missing payment confirmation.");

      const result = await confirmTipPayment({ paymentIntentId: piId });
      finishSuccess(result.amount || paidAmount);
    } catch (err) {
      setError(err.message || "Payment could not be completed.");
    } finally {
      setLoading(false);
    }
  };

  const cardLabel = savedCard
    ? `${formatCardBrand(savedCard.brand)} •••• ${savedCard.last4}`
    : null;

  if (!open) return null;

  return (
    <div className="tip-sheet-overlay" onClick={onClose} role="presentation">
      <div
        className="tip-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="tip-sheet-title"
      >
        <div className="tip-sheet-header">
          <div className="tip-sheet-header-dots" />
          <div className="tip-sheet-glow" />
          <div className="tip-sheet-jar-wrap">
            {showCoin && <div className="tip-sheet-coin tip-sheet-coin--animate" />}
            <TipJarSvg
              fillPercent={fillPercent}
              className={`tip-sheet-jar-svg${jarBounce ? " tip-sheet-jar-svg--bounce" : ""}`}
            />
          </div>
          <div className="tip-sheet-creator" id="tip-sheet-title">
            Leave a tip for {main}
            {em ? (
              <>
                {" "}
                <em>{em}</em>
              </>
            ) : null}
          </div>
          <div className="tip-sheet-handle">
            @{handle}
            {creator?.location ? ` · ${creator.location}` : ""}
            {meta.label ? ` · ${meta.label}` : ""}
          </div>
          <div className="tip-sheet-tagline">— your support means everything 🍁</div>
        </div>

        {phase === "select" && (
          <div className="tip-sheet-body">
            <div className="tip-sheet-label">Choose an amount</div>
            <div className="tip-sheet-options">
              {TIP_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  className={`tip-sheet-option${selected?.id === preset.id ? " tip-sheet-option--selected" : ""}`}
                  onClick={() => pickPreset(preset)}
                >
                  <div className={`tip-sheet-icon tip-sheet-icon--${preset.id}`}>
                    <span className="tip-sheet-emoji">{preset.emoji}</span>
                  </div>
                  <div className="tip-sheet-opt-name">{preset.label}</div>
                  <div className="tip-sheet-opt-price">${preset.amount}</div>
                  <div className="tip-sheet-opt-desc">{preset.desc}</div>
                </button>
              ))}
            </div>

            <div className="tip-sheet-divider">
              <span>or name your amount</span>
            </div>
            <div className="tip-sheet-custom-wrap">
              <span className="tip-sheet-prefix">$</span>
              <input
                className="tip-sheet-custom-input"
                type="number"
                min={TIP_LIMITS.minCustom}
                max={TIP_LIMITS.maxCustom}
                placeholder={`Any amount from $${TIP_LIMITS.minCustom} to $${TIP_LIMITS.maxCustom.toLocaleString()}`}
                value={customAmount}
                onChange={(e) => handleCustom(e.target.value)}
              />
              <span className="tip-sheet-suffix">CAD</span>
            </div>

            <button
              type="button"
              className="tip-sheet-anon"
              onClick={() => setAnonymous((v) => !v)}
            >
              <span className={`tip-sheet-toggle${anonymous ? " tip-sheet-toggle--on" : ""}`}>
                <span className="tip-sheet-knob" />
              </span>
              <span className="tip-sheet-anon-info">
                <span className="tip-sheet-anon-label">Tip anonymously</span>
                <span className="tip-sheet-anon-sub">
                  {creator.displayName?.split(" ")[0] || "Creator"} sees the amount — not your name
                </span>
              </span>
            </button>

            {error && <p className="tip-sheet-error">{error}</p>}

            <button
              type="button"
              className="tip-sheet-btn"
              disabled={!activeAmount || loading}
              onClick={handleSend}
            >
              {loading ? (savedCard ? "Sending…" : "Starting…") : "Send tip"}
              <span className="tip-sheet-btn-amt">
                {activeAmount
                  ? (cardLabel
                    ? `$${activeAmount} · ${cardLabel}`
                    : `$${activeAmount} CAD`)
                  : "— select an amount"}
              </span>
            </button>
            {savedCard ? (
              <button
                type="button"
                className="tip-sheet-back tip-sheet-alt-card"
                disabled={loading || !activeAmount}
                onClick={startElementCheckout}
              >
                Use a different card
              </button>
            ) : null}
          </div>
        )}

        {phase === "payment" && clientSecret && stripePromise && (
          <div className="tip-sheet-body">
            <div className="tip-sheet-label">Pay with card · ${paidAmount} CAD</div>
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: "stripe",
                  variables: {
                    colorPrimary: "#C4790A",
                    colorBackground: "#FFFFFF",
                    colorText: "#0E0C0A",
                    borderRadius: "10px",
                    fontFamily: "Inter, system-ui, sans-serif",
                  },
                },
              }}
            >
              <TipPaymentForm
                email={userEmail}
                onReady={setPaymentReady}
                onStripeRefs={setStripeRef}
              />
            </Elements>

            {error && <p className="tip-sheet-error">{error}</p>}

            <button
              type="button"
              className="tip-sheet-btn"
              disabled={loading || !paymentReady}
              onClick={handleConfirmPayment}
            >
              {loading ? "Processing…" : `Pay $${paidAmount} CAD`}
            </button>
            <button
              type="button"
              className="tip-sheet-back"
              disabled={loading}
              onClick={() => {
                setPhase("select");
                setClientSecret(null);
                setError("");
              }}
            >
              ← Back
            </button>
          </div>
        )}

        {phase === "payment" && (!clientSecret || !stripePromise) && (
          <div className="tip-sheet-body">
            <p className="tip-sheet-error">
              {error
                || (!publishableKey
                  ? "Add REACT_APP_STRIPE_PUBLISHABLE_KEY to enable Stripe."
                  : "Loading payment…")}
            </p>
            <button type="button" className="tip-sheet-back" onClick={onClose}>
              Close
            </button>
          </div>
        )}

        {phase === "success" && (
          <div className="tip-sheet-success tip-sheet-success--show">
            <div className="tip-sheet-success-ring">✓</div>
            <div className="tip-sheet-success-title">
              Tip <em>sent</em>
            </div>
            <p className="tip-sheet-success-body">
              {anonymous
                ? `Your anonymous $${paidAmount} tip is on its way.`
                : `Your $${paidAmount} tip is on its way — they'll see your name.`}
            </p>
            <span className="tip-sheet-success-hand">thank you for supporting their work 🍁</span>
            <button type="button" className="tip-sheet-btn" style={{ marginTop: 16 }} onClick={onClose}>
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function TipJarDropButton({ creator, onOpen, label }) {
  return (
    <button type="button" className="tip-drop-btn" onClick={onOpen}>
      <svg width="14" height="16" viewBox="0 0 72 80" fill="none" aria-hidden>
        <rect x="10" y="26" width="52" height="48" rx="10" fill="#FDF3E0" stroke="#C4790A" strokeWidth="2" />
        <rect x="10" y="11" width="52" height="11" rx="5.5" fill="#C4790A" />
      </svg>
      {label || "Tip"}
    </button>
  );
}
